import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { API, IDevice, IMerrcoDevice } from '@getgreenline/homi-shared'
import { FormError } from '../../../../components/FormError'
import { Typography, Modal, Button, Input, InputNumber, message } from 'antd'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { HelpLinks } from '../../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  device: IDevice
  onSave: () => void
  onHide: () => void
  currentUserStore?: CurrentUserStore
}

interface State extends IMerrcoDevice {
  isSaving?: boolean
  errorMessage?: string
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class ConfigureCardTerminalModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    const {
      merrcoMerchantNumber,
      merrcoMerchantTerminalNumber,
      merrcoApiAuthKey,
      merrcoCompanyNumber,
    } = props.device
    this.state = {
      isSaving: false,
      merrcoCompanyNumber,
      merrcoMerchantNumber,
      merrcoMerchantTerminalNumber,
      merrcoApiAuthKey: merrcoApiAuthKey || '',
    }
  }

  async save() {
    const company = this.props.currentCompanyStore!.company!
    this.setState({
      isSaving: true,
      errorMessage: undefined,
    })

    try {
      const merrcoUpdateObject: IMerrcoDevice = {
        merrcoMerchantNumber: this.state.merrcoMerchantNumber,
        merrcoMerchantTerminalNumber: this.state.merrcoMerchantTerminalNumber,
        merrcoApiAuthKey: this.state.merrcoApiAuthKey,
        merrcoCompanyNumber: this.state.merrcoCompanyNumber,
      }
      this.validateMerrcoDevice(merrcoUpdateObject)
      await API.updateMerrcoDevice(company.id, this.props.device.id, merrcoUpdateObject)

      message.success(`Successfully updated terminal`, 1)

      this.props.onSave()
    } catch (error) {
      this.setState({
        isSaving: false,
        errorMessage: parseErrorMsg(error) || 'There was an error updating',
      })
    }
  }

  validateMerrcoDevice(device: IMerrcoDevice) {
    const {
      merrcoMerchantNumber,
      merrcoMerchantTerminalNumber,
      merrcoApiAuthKey,
      merrcoCompanyNumber,
    } = device
    if (merrcoMerchantNumber && merrcoMerchantNumber.toString().length > 8) {
      throw new Error('Merchant Number can not be longer than 8 characters')
    }
    if (merrcoMerchantTerminalNumber && merrcoMerchantTerminalNumber.toString().length > 5) {
      throw new Error('Merchant Terminal Number can not be longer than 5 characters')
    }
    if (merrcoCompanyNumber && merrcoCompanyNumber.toString().length > 5) {
      throw new Error('Merrco Company Number can not be longer than 5 characters')
    }
  }

  render() {
    const {
      merrcoMerchantNumber,
      merrcoMerchantTerminalNumber,
      merrcoApiAuthKey,
      merrcoCompanyNumber,
    } = this.state
    const { currentUserStore } = this.props

    return (
      <Modal
        title='Configure Merrco terminal'
        visible={true}
        onCancel={this.props.onHide}
        onOk={this.save}
        footer={[
          <Button key='cancel' onClick={this.props.onHide}>
            Cancel
          </Button>,
          <Button
            key='save'
            type='primary'
            loading={this.state.isSaving}
            disabled={
              !merrcoMerchantNumber || !merrcoMerchantTerminalNumber || !merrcoCompanyNumber
            }
            onClick={() => this.save()}
          >
            Save
          </Button>,
        ]}
      >
        <div>
          <Typography.Text type='secondary'>
            Setup instructions can be found{' '}
            <a target='_blank' href={HelpLinks.MERRCO_TERMINAL_SETUP} rel='noreferrer'>
              here
            </a>
            .
          </Typography.Text>
        </div>
        <br />
        <div className='form-group'>
          <label className='form-label'>Company number</label>
          <InputNumber
            style={{ width: '100%' }}
            type='number'
            value={this.state.merrcoCompanyNumber || undefined}
            onChange={(number) => this.setState({ merrcoCompanyNumber: number || null })}
          />
        </div>
        <div className='form-group'>
          <label className='form-label'>Merchant number</label>
          <InputNumber
            style={{ width: '100%' }}
            value={this.state.merrcoMerchantNumber || undefined}
            onChange={(number) => this.setState({ merrcoMerchantNumber: number || null })}
          />
        </div>
        <div className='form-group'>
          <label className='form-label'>Merchant terminal number</label>
          <InputNumber
            style={{ width: '100%' }}
            value={this.state.merrcoMerchantTerminalNumber || undefined}
            onChange={(number) => this.setState({ merrcoMerchantTerminalNumber: number || null })}
          />
        </div>
        {currentUserStore?.currentUser?.isAdmin && (
          <div className='form-group'>
            <label className='form-label'>Merrco API Auth Key (For Developer Use only)</label>
            <Input
              value={this.state.merrcoApiAuthKey || undefined}
              onChange={(e) => this.setState({ merrcoApiAuthKey: e.target.value })}
            />
          </div>
        )}
        <FormError message={this.state.errorMessage} />
      </Modal>
    )
  }
}
